/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';
import { task, timeout } from 'ember-concurrency';
import { stringIncludes } from 'cio/utils/string';
import config from 'cio/config/environment';
import { groupMessages, orderMessages } from 'cio/utils/conditions/action-grouping';
import ActionCondition from 'cio/app-models/conditions/action';
import FiltersService, { FilterMessage } from 'cio/services/filters';
import FeaturesService from 'ember-feature-flags/services/features';
import { Channel } from 'cio/utils/metrics/common';
import { tracked } from '@glimmer/tracking';

const PER_PAGE = 50;
const DEBOUNCE_DELAY = config.APP.shortDelay;

interface Args {
  condition: ActionCondition;
  disabled: boolean;
  fluid: boolean;
  size: string;
  variation?: string;
  allowAllVariationsSelection?: boolean;
  allowWildcardSelection?: boolean;
  allowVariationSelection?: boolean;
  allowInParentWildcardSelection?: boolean;
}

export default class Message extends Component<Args> {
  @service filters!: FiltersService;
  @service features!: FeaturesService;

  @tracked virtualSelectPage = 1;
  @tracked previousSearchText = '';

  @computed('orderedMessages.[]', 'args.condition.action')
  get selectedMessage() {
    if (this.args.condition?.action == null) {
      return null;
    }

    return this.orderedMessages.find((m) => m.output_id === this.args.condition.action);
  }

  @computed('args.condition.isAny')
  get isDeleted() {
    if (this.args.condition?.action == null) {
      return false;
    }

    let segmentableMessages = this.filters.segmentable.filterBy('type', this.args.condition.type as Channel);
    let outputId = this.args.condition.action;
    return (
      (this.args.condition.isAny == null || this.args.condition.isAny === false) &&
      isNone(segmentableMessages.findBy('output_id', outputId))
    );
  }

  @computed(
    'args.condition.{type,action}',
    'args.{allowVariationSelection,allowWildcardSelection,allowInParentWildcardSelection}',
  )
  get orderedMessages() {
    let { type, action } = this.args.condition;
    let actions = this.filters.segmentable.filterBy('type', type as Channel) as FilterMessage[];

    if (this.args.allowVariationSelection == null || this.args.allowVariationSelection === false) {
      actions = actions.filter(
        (action: any) => action?.is_segmentable_variation == null || action?.is_segmentable_variation === false,
      );
    }

    // Opsbug https://github.com/customerio/opsbugs/issues/3902
    // Remove option "All Variations"
    if (this.args.allowAllVariationsSelection === false) {
      actions = actions.filter((a) => a.isLanguageBranch !== true);
    }

    return orderMessages(
      actions,
      action,
      this.isDeleted,
      Boolean(this.args.allowWildcardSelection),
      type === 'email' || Boolean(this.args.allowInParentWildcardSelection),
      type as Channel,
    );
  }

  // Potentially, there may be thousands of messages shown in the dropdown. We
  // don't want to render them all at once, so we'll use the fly-power-select
  // component's "search" and "onScrollEnd" functionalities to perform a
  // "virtual" query. We don't hit an API during these searches, we'll perform
  // the filtering right here in JavaScript.
  get initialSelectOptions() {
    return groupMessages(this.orderedMessages.slice(0, PER_PAGE));
  }

  @action
  onSelect(item: any) {
    let outputId = item.output_id;

    if (item.isLanguageBranch != null) {
      outputId = `output_${item.id}`;
      this.args.condition.set('uiType', item.type);
    }

    this.args.condition.setProperties({
      action: outputId,
      campaignFirstStarted: item.parent?.campaignFirstStarted,
    });
    if (!this.args.condition.canRefine && this.args.condition.refinement != null) {
      this.args.condition.set('refinement', null);
    }
  }

  @action
  virtualQuery(searchText: string, page: number) {
    let start = PER_PAGE * (page - 1);

    return groupMessages(
      this.orderedMessages
        .filter((message) => stringIncludes(message.searchLabel as string, searchText))
        .slice(start, start + PER_PAGE),
    );
  }

  selectOnScrollEnd = task({ restartable: true }, async (searchText: string) => {
    // ember power select doesn't call the "search" function when resetting
    // search back to an empty string, which can mess up our "virtual page"
    // tracking.
    // If the page is larger than 1, and the new search text is an empty string,
    // but the old search text wasn't, it means that the user has emptied the
    // search box, and then scrolled to the end of the list. We need to reset
    // the page back to the start in this case.
    if (this.virtualSelectPage > 1 && searchText === '' && this.previousSearchText !== searchText) {
      this.virtualSelectPage = 1;
    }

    this.virtualSelectPage += 1;
    this.previousSearchText = searchText;
    await timeout(DEBOUNCE_DELAY);
    return this.virtualQuery(searchText, this.virtualSelectPage);
  });

  paginatedSelectQuery = task({ restartable: true }, async (searchText: string) => {
    this.virtualSelectPage = 1;
    this.previousSearchText = searchText;

    await timeout(DEBOUNCE_DELAY);
    return this.virtualQuery(searchText, this.virtualSelectPage);
  });
}
